import React, { useState, FormEvent, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { Button, TextField, Box, Container, Typography, Alert, Paper, InputAdornment, IconButton, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { LogoMark, LogoWordmark } from './brand/Logo';

// Define expected response data structure
interface TokenResponse {
    access_token: string;
    token_type: string;
}

// (Removed unused ErrorDetail type)

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper type guard focusing only on the flag
function isAxiosError(error: any): error is { isAxiosError: true; response?: any; request?: any; message?: string } {
    return typeof error === 'object' && error !== null && error.isAxiosError === true;
}

function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const auth = useContext(AuthContext);
    const theme = useTheme();

    if (!auth) {
        throw new Error("Login component must be used within an AuthProvider");
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(null);

        const params = new URLSearchParams();
        params.append('username', username);
        params.append('password', password);

        try {
            const response = await axios.post<TokenResponse>(`${API_URL}/api/token`, params, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });

            if (response.data.access_token) {
                auth.login(response.data.access_token);
                navigate('/');
            }
        } catch (err: unknown) {
            if (isAxiosError(err)) {
                if (err.response) {
                    const status = err.response.status;
                    const errorData: any = err.response.data;
                    if (status === 401) {
                        setError('Nom d\'utilisateur ou mot de passe incorrect.');
                    } else {
                        const detail = typeof errorData?.detail === 'string' ? errorData.detail : 'Erreur serveur';
                        setError(`Erreur de connexion: ${detail}`);
                    }
                } else if (err.request) {
                    setError('Erreur de connexion. Pas de réponse du serveur.');
                    console.error("Erreur de connexion - pas de réponse:", err.request);
                } else {
                    setError('Erreur de connexion. Erreur lors de la configuration de la requête.');
                    console.error('Erreur de connexion - configuration:', err.message);
                }
            } else {
                if (err instanceof Error) {
                    setError('Une erreur inattendue s\'est produite: ' + err.message);
                } else {
                    setError('Une erreur inattendue et inconnue s\'est produite.');
                }
                console.error("Erreur de connexion non-Axios:", err);
            }
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // dvh accounts for the mobile browser's collapsing toolbar.
                    minHeight: '100dvh',
                    py: 4,
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, sm: 4 },
                        width: '100%',
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                        boxShadow: '0 20px 45px -25px rgba(17,24,39,0.35)',
                    }}
                >
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mb: 3,
                        }}
                    >
                        <LogoMark size={56} />
                        <LogoWordmark sx={{ fontSize: '1.75rem', mt: 1.5 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                            Suivi des auxiliaires de vie et des heures PCH
                        </Typography>
                    </Box>
                    </motion.div>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: 0.05 }}>
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="username"
                            label="Nom d'utilisateur"
                            name="username"
                            autoComplete="username"
                            autoFocus
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Mot de passe"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 3 }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{
                                mt: 1,
                                mb: 2,
                                py: 1.5,
                                borderRadius: 2,
                                background: 'linear-gradient(90deg, #6366F1 0%, #4338CA 100%)'
                            }}
                        >
                            Se connecter
                        </Button>
                    </Box>
                    </motion.div>
                </Paper>

                <Box
                    component="footer"
                    sx={{
                        py: 2,
                        mt: 'auto',
                        textAlign: 'center',
                        backgroundColor: theme.palette.background.paper,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        width: '100%',
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        © Yannis Tevissen 2025
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
}

export default Login; 